import { useRef, useLayoutEffect, useState, useEffect } from 'react'
import { UserLevel } from '@ds'
import { BattlePassLevel, useBattlePass } from '@store'

import styles from './Path.module.scss'

type Props = {
  levelItems: {
    level: number
    default: BattlePassLevel | null
    premium: BattlePassLevel | null
  }[]
  level: number
  progress: number
}

export const Path = ({ levelItems, level, progress }: Props) => {
  const totalSteps = levelItems.length
  const currentIndex = levelItems.findLastIndex((item) => item.level === level)
  const clampedIndex = Math.max(0, currentIndex)
  const pathProgress = ((clampedIndex + progress) / totalSteps) * 100

  const userLevelsRef = useRef<HTMLDivElement>(null)
  const [levelsHeight, setLevelsHeight] = useState(0)

  const battlePassItems = useBattlePass((state) => state.data?.levels || [])

  useLayoutEffect(() => {
    if (userLevelsRef.current) {
      setLevelsHeight(userLevelsRef.current.clientHeight)
    }
  }, [levelItems])

  useEffect(() => {
    if (!userLevelsRef.current) return
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setLevelsHeight(entry.contentRect.height)
      }
    })
    observer.observe(userLevelsRef.current)

    return () => observer.disconnect()
  }, [])

  if (battlePassItems.length === 0) {
    return null
  }

  return (
    <div className={styles.container}>
      <div className={styles.pathStart} />
      <div className={styles.path}>
        <div className={styles.baseProgress} />
        <div
          className={styles.pathLine}
          style={{ height: `${levelsHeight}px` }}
        >
          <div
            className={styles.progress}
            style={{ height: `${pathProgress}%` }}
          />
          <svg
            viewBox="0 0 29 30"
            fill="none"
            className={styles.starIcon}
            style={{ top: `${pathProgress}%` }}
          >
            <g filter="url(#filter0_d_133_1270)">
              <path
                d="M14.32 2.63C14.32 2.63 14.45 12.11 15.70 13.35C16.94 14.60 26.42 14.74 26.42 14.74C26.42 14.74 16.94 14.87 15.70 16.12C14.45 17.36 14.31 26.83 14.31 26.83C14.31 26.83 14.18 17.36 12.94 16.11C11.69 14.87 2.22 14.73 2.22 14.73C2.22 14.73 11.69 14.60 12.94 13.35C14.18 12.11 14.32 2.63 14.32 2.63Z"
                fill="white"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_133_1270"
                x="0.02"
                y="0.44"
                width="28.59"
                height="28.59"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="1.10" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.71 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_133_1270"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_133_1270"
                  result="shape"
                />
              </filter>
            </defs>
          </svg>
        </div>
        <div className={styles.userLevels} ref={userLevelsRef}>
          {levelItems.map((item, index) => (
            <UserLevel
              key={index}
              level={item.level}
              active={index <= clampedIndex}
              disabled={index > clampedIndex}
              className={styles.userLevel}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
